'use client';

import type { AnalyticsType } from '@ria-client/segment/types';
import {
  BankingSubProductTypeEnum,
  type MonetizableEnum,
} from '@ria-client/supergraph/marketplace/enums';
import type { DriverType } from '@ria-client/supergraph/marketplace/types';
import type {
  CtaType,
  ImageType,
  ProductCard as ProductCardType,
} from '@ria-client/supergraph/roundup/types';
import type { StructuredContentDocument } from '@ria-client/supergraph/structuredContent/types';
import { useBestOfAwardsConfig } from '../BestOfAwards/context';
import { BestOfAwardsRoundupBanner } from '../BestOfAwards/roundup-banner';
import { CtaName } from '../nodes/financial-advisors-roundup-sections/cta-name';
import { Driver } from '../nodes/financial-advisors-roundup-sections/driver';
import { ImageCtaReviewSection } from '../nodes/financial-advisors-roundup-sections/image-cta-review-section';
import { OurTake } from '../nodes/financial-advisors-roundup-sections/our-take';

export type ProductCardProps = {
  cardBadgeText?: string;
  ctaAnalytics: AnalyticsType;
  bottomLine?: StructuredContentDocument;
  cta?: CtaType;
  displayName?: string;
  drivers: DriverType[];
  federallyInsuredAnnotation?: string;
  image?: ImageType;
  monetizable: MonetizableEnum;
  starRating?: number;
  starRatingTooltip?: string;
  reviewLink?: string;
  isFidelityOffer?: boolean;
  isRiaOffer?: boolean;
  isSeoBankingFFActive: boolean;
  productType?: string;
  periodConsideredDate?: string;
  reviewedAtDate?: string;
  riaDisclaimerText?: string;
  subProductType?: string;
  pros?: StructuredContentDocument[];
  cons?: StructuredContentDocument[];
  structuredContentTagline?: StructuredContentDocument;
  oneYearApyEntity?: ProductCardType['oneYearApyEntity'];
  threeYearApyEntity?: ProductCardType['threeYearApyEntity'];
  fiveYearApyEntity?: ProductCardType['fiveYearApyEntity'];
};

export function ProductCard({
  ctaAnalytics,
  bottomLine,
  cta,
  displayName,
  drivers,
  image,
  monetizable,
  starRating,
  starRatingTooltip,
  reviewLink,
  isFidelityOffer = false,
  isRiaOffer = false,
  isSeoBankingFFActive,
  periodConsideredDate,
  productType,
  reviewedAtDate,
  riaDisclaimerText,
  subProductType,
  pros,
  cons,
  structuredContentTagline,
  oneYearApyEntity,
  threeYearApyEntity,
  fiveYearApyEntity,
}: ProductCardProps) {
  const { config, hasBestOfAward } = useBestOfAwardsConfig(ctaAnalytics.offerId);

  return (
    <div className="border-2 border-border" data-testid="product-card">
      <div className="mt-3 px-3 pb-0 md:px-4 md:pb-3">
        {hasBestOfAward && (
          <BestOfAwardsRoundupBanner className="mb-3" variant="card">
            {config.awardText}
          </BestOfAwardsRoundupBanner>
        )}
        <div className="mb-3">
          {cta ? (
            <CtaName cta={cta} ctaAnalytics={ctaAnalytics} displayName={displayName} />
          ) : (
            <p className="typography-text-large-bold">{displayName}</p>
          )}
        </div>
        <div className="flex flex-col md:flex-row">
          <ImageCtaReviewSection
            cta={cta}
            ctaAnalytics={ctaAnalytics}
            displayName={displayName}
            image={image}
            isFidelityOffer={isFidelityOffer}
            isRiaOffer={isRiaOffer}
            monetizable={monetizable}
            periodConsideredDate={periodConsideredDate}
            reviewLink={reviewLink}
            reviewedAtDate={reviewedAtDate}
            riaDisclaimerText={riaDisclaimerText}
            starRating={starRating}
            starRatingTooltip={starRatingTooltip}
          />
          <div className="mt-3 flex flex-1 flex-col md:mt-0">
            {drivers.map((driver, index) => (
              <Driver
                borderBottom={index !== drivers.length - 1}
                driver={driver}
                fiveYearApyEntity={fiveYearApyEntity}
                key={driver.label}
                oneYearApyEntity={oneYearApyEntity}
                renderCdApys={
                  subProductType === BankingSubProductTypeEnum.CD && driver.id?.includes('apy')
                }
                threeYearApyEntity={threeYearApyEntity}
              />
            ))}
          </div>
        </div>
      </div>
      <OurTake
        bottomLine={bottomLine}
        cons={cons}
        cta={cta}
        ctaAnalytics={ctaAnalytics}
        isDefaultExpanded={isSeoBankingFFActive}
        productType={productType}
        pros={pros}
        structuredContentTagline={structuredContentTagline}
      />
    </div>
  );
}
